import { Injectable } from '@angular/core';
import { Observable, from, of, map, catchError } from 'rxjs';

export interface GeocodeResult {
  /** Dirección corta para mostrar y guardar (calle, comuna). */
  displayName: string;
  /** Texto completo de Nominatim (solo referencia interna/UI secundaria). */
  fullName: string;
  lat: number;
  lng: number;
}

interface NominatimAddress {
  road?: string;
  pedestrian?: string;
  street?: string;
  highway?: string;
  house_number?: string;
  suburb?: string;
  neighbourhood?: string;
  city_district?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

interface NominatimItem {
  display_name: string;
  lat: string;
  lon: string;
  address?: NominatimAddress;
}

const NOMINATIM = 'https://nominatim.openstreetmap.org';

@Injectable({ providedIn: 'root' })
export class GeocodeService {
  searchChile(query: string, limit = 5): Observable<GeocodeResult[]> {
    const q = query?.trim();
    if (!q || q.length < 3) return of([]);

    const params = new URLSearchParams({
      q,
      format: 'json',
      addressdetails: '1',
      limit: String(limit),
      countrycodes: 'cl',
      'accept-language': 'es'
    });

    return from(
      fetch(`${NOMINATIM}/search?${params}`, {
        headers: { Accept: 'application/json' }
      }).then((res) => {
        if (!res.ok) throw new Error('geocode search failed');
        return res.json() as Promise<NominatimItem[]>;
      })
    ).pipe(
      map((items) => (items ?? []).map((i) => this._toResult(i))),
      catchError(() => of([]))
    );
  }

  reverse(lat: number, lng: number): Observable<GeocodeResult | null> {
    const params = new URLSearchParams({
      lat: String(lat),
      lon: String(lng),
      format: 'json',
      addressdetails: '1',
      'accept-language': 'es'
    });

    return from(
      fetch(`${NOMINATIM}/reverse?${params}`, {
        headers: { Accept: 'application/json' }
      }).then((res) => {
        if (!res.ok) throw new Error('geocode reverse failed');
        return res.json() as Promise<NominatimItem>;
      })
    ).pipe(
      map((item) => (item?.lat && item?.lon ? this._toResult(item) : null)),
      catchError(() => of(null))
    );
  }

  private _toResult(item: NominatimItem): GeocodeResult {
    const fullName = item.display_name;
    return {
      displayName: this._formatShortAddress(item),
      fullName,
      lat: Number(item.lat),
      lng: Number(item.lon)
    };
  }

  /** Ej: "Av. Colín 123, Talca" en vez del display_name completo de Nominatim. */
  private _formatShortAddress(item: NominatimItem): string {
    const a = item.address ?? {};
    const road = a.road || a.pedestrian || a.street || a.highway;
    const street = [road, a.house_number].filter(Boolean).join(' ').trim();
    const locality =
      a.city ||
      a.town ||
      a.village ||
      a.municipality ||
      a.city_district ||
      a.suburb ||
      a.neighbourhood ||
      a.county;

    const parts = [street, locality].filter((p): p is string => !!p?.trim());
    if (parts.length) return parts.join(', ');

    return item.display_name
      .split(',')
      .slice(0, 2)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');
  }
}
